import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import './wallet.css';

// Admin queue for client payout requests (wallet cash → manual bank transfer).
// The owner pays from the bank, then marks paid (bank reference required) — the
// wallet is debited at THAT moment. Decline releases the reservation.
//
// TWO-PHASE MARK PAID (backend: routes/payouts.js /admin/:id/claim + /release).
// Opening the Mark Paid modal CLAIMS the row ('processing'); closing it without
// paying RELEASES it. Without the claim, the window between "manager copies the
// IBAN" and "manager marks paid" is unprotected: the client can cancel the
// request in it, and a second manager sees the row as available and can send the
// same transfer again. /admin/pending now returns 'processing' rows precisely so
// a claimed payout is visible-but-not-actionable rather than invisibly stuck, so
// this file has to render that state.
export default function PendingPayoutsSection({ onChanged, onCountChange }) {
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null); // { type: 'paid'|'decline', payout, claimed }
    const [bankReference, setBankReference] = useState('');
    const [declineNote, setDeclineNote] = useState('');
    const [processing, setProcessing] = useState(false);
    const [claiming, setClaiming] = useState(false);
    // Set when /pay answers 409 DEBIT_FAILED_AFTER_TRANSFER: the bank transfer
    // WENT OUT but the wallet debit failed and the row is HELD in 'processing'.
    // This cannot be a toast — it is the one outcome where real money has left
    // the business and nothing balanced it.
    const [debitFailure, setDebitFailure] = useState(null);
    // Live wallet figures for the payout being actioned. A queue row may be days
    // old; by the time the owner transfers, the client's cash can be entirely spent,
    // and the only feedback used to be a 400 AFTER the bank transfer cleared.
    // Fetched fresh on modal open (GET /api/wallet/clients/:id is wallet-staff
    // authz'd and read-only). null = not loaded yet, so we never render a stale
    // or invented balance next to a real transfer decision.
    const [walletInfo, setWalletInfo] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const [walletError, setWalletError] = useState('');

    // Mirrors authorizeWalletStaff on /api/payouts/admin/* (admin + manager +
    // supervisor) — see W1. Gating the UI more tightly than the backend just
    // removes an ability from admins on deploy day without telling anyone. If
    // The owner later wants money-moving decisions to be manager-only, narrow
    // the BACKEND gate and this constant together.
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const canActOnPayouts = ['manager', 'admin', 'supervisor'].includes(userRole);
    // Who am I — so a row THIS manager claimed reads as resumable rather than
    // as "someone else is on it".
    const myUserId = String(localStorage.getItem('userId') || '');
    const isProcessing = (p) => String(p.status || '') === 'processing';
    const claimedByMe = (p) => isProcessing(p) && myUserId && String(p.processing_by || '') === myUserId;
    const claimedByOther = (p) => isProcessing(p) && !claimedByMe(p);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (d) => (d ? DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm') : '—');

    const fetchPayouts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/payouts/admin/pending');
            setPayouts(Array.isArray(res.data) ? res.data : (res.data?.payouts || []));
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error fetching pending payouts:', err);
            setPayouts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPayouts();
    }, [fetchPayouts]);

    useEffect(() => {
        if (!loading && onCountChange) onCountChange(payouts.length);
    }, [loading, payouts.length, onCountChange]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(String(text)).then(
            () => toast.success('Copied to clipboard'),
            () => toast.error('Failed to copy')
        );
    };

    const resetModalState = () => {
        setAction(null);
        setBankReference('');
        setDeclineNote('');
        setWalletInfo(null);
        setWalletError('');
    };

    // Closing the Mark Paid modal without paying RELEASES the claim, so the row
    // goes back into the queue for whoever picks it up next (and the client can
    // cancel again). Best-effort: a failed release only leaves the row claimed by
    // this manager, which is the safe direction — it is never silently payable
    // by a second manager as a result.
    const closeModal = async () => {
        const current = action;
        resetModalState();
        if (current?.type === 'paid' && current.claimed) {
            try {
                await axios.post(`/api/payouts/admin/${current.payout.id}/release`, {});
            } catch (err) {
                console.error('Error releasing the payout claim:', err);
                toast.error('The payout stayed claimed by you — reopen it to pay or release it.');
            }
            await fetchPayouts();
        }
    };

    const loadWallet = useCallback(async (clientId) => {
        setWalletInfo(null);
        setWalletError('');
        if (!clientId) return;
        setWalletLoading(true);
        try {
            const res = await axios.get(`/api/wallet/clients/${clientId}`);
            setWalletInfo(res.data?.wallet || null);
        } catch (err) {
            console.error('Error loading client wallet for payout:', err);
            setWalletError('Could not load the client’s live wallet balance — verify it on the client page before transferring.');
        } finally {
            setWalletLoading(false);
        }
    }, []);

    // Open the confirm modal AND load the client's live wallet. Money decisions
    // get current figures, not the ones implied by a stale queue row.
    //
    // For Mark Paid the row is CLAIMED first and the modal only opens if the
    // claim won. A lost claim (409 ALREADY_PROCESSING) means another manager is
    // mid-transfer: the modal must not open at all, because the IBAN it shows is
    // an invitation to send the same money twice.
    const openAction = useCallback(async (type, payout) => {
        if (type !== 'paid') {
            setAction({ type, payout, claimed: false });
            await loadWallet(payout.client_id);
            return;
        }

        setClaiming(true);
        let claimed = false;
        try {
            // A row this manager already holds is being resumed, not re-claimed;
            // claim would 409 on it (it is no longer 'requested').
            if (!claimedByMe(payout)) {
                await axios.post(`/api/payouts/admin/${payout.id}/claim`, {});
            }
            claimed = true;
        } catch (err) {
            const data = err.response?.data || {};
            if (data.code === 'ALREADY_PROCESSING') {
                toast.error(data.error || 'Another manager is already processing this payout. Do not transfer.');
            } else {
                toast.error(data.error || 'Could not claim this payout — it may have been cancelled or already paid.');
            }
            setClaiming(false);
            await fetchPayouts();
            return;
        }
        setClaiming(false);
        setAction({ type, payout, claimed });
        await loadWallet(payout.client_id);
    }, [loadWallet, fetchPayouts, myUserId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Release a claim from the list (the manager abandoned the transfer and did
    // not use the modal's Cancel).
    const releaseClaim = async (payout) => {
        try {
            await axios.post(`/api/payouts/admin/${payout.id}/release`, {});
            toast.success('Payout returned to the queue.');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not release this payout.');
        }
        await fetchPayouts();
    };

    const handleMarkPaid = async () => {
        if (!action) return;
        if (!bankReference.trim()) {
            toast.error('Bank reference is required — it goes on the ledger entry.');
            return;
        }
        setProcessing(true);
        try {
            await axios.post(`/api/payouts/admin/${action.payout.id}/pay`, {
                bank_reference: bankReference.trim(),
            });
            toast.success(`Payout of ${money(action.payout.amount)} marked paid — wallet debited.`);
            resetModalState(); // paid: the claim is consumed, never release it
            await fetchPayouts();
            if (onChanged) onChanged();
        } catch (err) {
            const data = err.response?.data || {};
            console.error('Error marking payout paid:', err);
            if (data.code === 'DEBIT_FAILED_AFTER_TRANSFER') {
                // The transfer is gone and the wallet was NOT debited. Do not
                // release the claim (the row is deliberately held) and do not
                // let this disappear as a toast.
                setDebitFailure({
                    payout: action.payout,
                    bankReference: bankReference.trim(),
                    error: data.error || 'The wallet could not be debited after the transfer.',
                });
                resetModalState();
                await fetchPayouts();
                if (onChanged) onChanged();
                return;
            }
            if (data.code === 'ALREADY_PROCESSING') {
                toast.error(data.error || 'Another manager claimed this payout. Do not transfer.');
                resetModalState();
                await fetchPayouts();
                return;
            }
            toast.error(data.error || 'Failed to mark the payout paid');
        } finally {
            setProcessing(false);
        }
    };

    const handleDecline = async () => {
        if (!action) return;
        if (!declineNote.trim()) {
            toast.error('Please add a note — the client sees why it was declined.');
            return;
        }
        setProcessing(true);
        try {
            await axios.post(`/api/payouts/admin/${action.payout.id}/decline`, {
                note: declineNote.trim(),
            });
            toast.success('Payout declined — the reserved amount is spendable again.');
            closeModal();
            await fetchPayouts();
            if (onChanged) onChanged();
        } catch (err) {
            console.error('Error declining payout:', err);
            toast.error(err.response?.data?.error || 'Failed to decline the payout');
        } finally {
            setProcessing(false);
        }
    };

    // No payouts waiting: render nothing (owner, 2026-09-07) — the overview
    // row at the top of the page is where "nothing pending" is said.
    if (!loading && payouts.length === 0) return null;

    return (
        <div className="swallet__section">
            <div className="swallet__sectionHead">
                <h2 className="swallet__sectionTitle">Pending Payouts</h2>
                <span className="swallet__sectionSub">
                    Pay manually by bank transfer, then mark paid — the wallet is debited at that moment.
                </span>
            </div>

            {loading ? (
                <div className="swallet__empty">Loading...</div>
            ) : payouts.length === 0 ? (
                <div className="swallet__empty">No pending payouts.</div>
            ) : (
                <table className="swallet__table">
                    <thead>
                        <tr>
                            <th>Requested</th>
                            <th>Client</th>
                            <th>Amount</th>
                            <th>IBAN</th>
                            <th>Account Name</th>
                            <th>Bank</th>
                            <th>Note</th>
                            {canActOnPayouts && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {payouts.map((p) => (
                            <tr key={p.id}>
                                <td>{formatDate(p.requested_at || p.created_at)}</td>
                                <td style={{ fontWeight: 900 }}>
                                    <Link to={`/wallet/clients/${p.client_id}`} className="swallet__proofLink">
                                        {p.client_name || `Client #${p.client_id}`}
                                    </Link>
                                </td>
                                <td style={{ fontWeight: 900 }}>{money(p.amount)}</td>
                                <td>
                                    <span className="swallet__copyCell">
                                        <span className="swallet__copyValue">{p.iban || '—'}</span>
                                        {p.iban && (
                                            <button className="swallet__copyBtn" onClick={() => copyToClipboard(p.iban)}>
                                                Copy
                                            </button>
                                        )}
                                    </span>
                                </td>
                                <td>
                                    <span className="swallet__copyCell">
                                        <span className="swallet__copyValue">{p.account_name || '—'}</span>
                                        {p.account_name && (
                                            <button className="swallet__copyBtn" onClick={() => copyToClipboard(p.account_name)}>
                                                Copy
                                            </button>
                                        )}
                                    </span>
                                </td>
                                <td>{p.bank_name || '—'}</td>
                                <td>{p.note || '—'}</td>
                                {canActOnPayouts && (
                                    <td>
                                        {/* A row another manager is mid-transfer on must NOT look
                                            available — that is how the same payout gets sent twice. */}
                                        {claimedByOther(p) ? (
                                            <div className="swallet__payoutClaim">
                                                <span className="swallet__chip swallet__chip--orange">
                                                    Being processed by {p.processing_by_name || 'another manager'}
                                                </span>
                                                <span className="swallet__payoutClaimHint">
                                                    Do not transfer. It returns to the queue if they cancel.
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="swallet__btnGroup">
                                                {claimedByMe(p) && (
                                                    <span className="swallet__chip swallet__chip--blue">
                                                        Claimed by you
                                                    </span>
                                                )}
                                                <button
                                                    className="swallet__btn swallet__btn--success"
                                                    disabled={claiming}
                                                    onClick={() => openAction('paid', p)}
                                                >
                                                    {claimedByMe(p) ? 'Resume Mark Paid' : 'Mark Paid'}
                                                </button>
                                                {claimedByMe(p) && (
                                                    <button
                                                        className="swallet__btn"
                                                        onClick={() => releaseClaim(p)}
                                                    >
                                                        Release
                                                    </button>
                                                )}
                                                <button
                                                    className="swallet__btn swallet__btn--danger"
                                                    onClick={() => openAction('decline', p)}
                                                >
                                                    Decline
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {action && (
                <div className="swallet__overlay" onClick={(e) => { if (e.target === e.currentTarget && !processing) closeModal(); }}>
                    <div className="swallet__modal">
                        {action.type === 'paid' && (
                            <>
                                <h3 className="swallet__modalTitle">Mark Payout Paid</h3>
                                <p className="swallet__modalText">
                                    Confirm the bank transfer of <strong>{money(action.payout.amount)}</strong> to{' '}
                                    <strong>{action.payout.account_name || action.payout.client_name}</strong> was sent.
                                    This debits the client&apos;s wallet cash now and records the ledger entry.
                                </p>
                                {action.claimed && (
                                    <div className="swallet__payoutNote">
                                        This payout is now <strong>claimed by you</strong>: no other manager can pay it and
                                        the client cannot cancel it while this window is open. Cancel below to return it to
                                        the queue if you do not send the transfer.
                                    </div>
                                )}

                                {/* Live wallet, fetched when this modal opened. The transfer is
                                    irreversible once sent, so the drained-wallet case has to be
                                    visible BEFORE the money leaves — not as a 400 afterwards. */}
                                {walletLoading && (
                                    <div className="swallet__payoutNote">Loading the client’s live wallet balance…</div>
                                )}
                                {walletError && <div className="swallet__payoutWarn">{walletError}</div>}
                                {walletInfo && (
                                    <>
                                        {Number(walletInfo.cash_balance || 0) + 0.005 < Number(action.payout.amount || 0) && (
                                            <div className="swallet__payoutWarn">
                                                This payout can no longer be debited in full — the client’s cash balance is{' '}
                                                {money(walletInfo.cash_balance)} against a requested {money(action.payout.amount)}.
                                                DO NOT transfer: marking it paid will be rejected, and any money you have already
                                                sent cannot be recovered from the wallet.
                                            </div>
                                        )}
                                        <div className="swallet__payoutBalances">
                                            <div className="swallet__payoutBalance">
                                                <div className="swallet__payoutBalanceLabel">Cash balance (live)</div>
                                                <div
                                                    className={`swallet__payoutBalanceValue${
                                                        Number(walletInfo.cash_balance || 0) + 0.005 < Number(action.payout.amount || 0)
                                                            ? ' swallet__payoutBalanceValue--short' : ''
                                                    }`}
                                                >
                                                    {money(walletInfo.cash_balance)}
                                                </div>
                                            </div>
                                            {action.payout.withdrawable_balance != null && (
                                                <div className="swallet__payoutBalance">
                                                    <div className="swallet__payoutBalanceLabel">Withdrawable</div>
                                                    <div className="swallet__payoutBalanceValue">
                                                        {money(action.payout.withdrawable_balance)}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="swallet__payoutBalance">
                                                <div className="swallet__payoutBalanceLabel">Bonus balance</div>
                                                <div className="swallet__payoutBalanceValue">{money(walletInfo.bonus_balance)}</div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* The clawback the client will suffer — their own UI already
                                    shows this figure, so the manager must see it too. */}
                                {Number(action.payout.bonus_removed_preview || 0) > 0.005 && (
                                    <div className="swallet__payoutNote">
                                        Paying this forfeits {money(action.payout.bonus_removed_preview)} of the client’s
                                        unused bonus (top-up bonus clawback). The client has been shown this figure.
                                    </div>
                                )}
                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">Bank Reference (required)</label>
                                    <input
                                        type="text"
                                        className="swallet__formInput"
                                        value={bankReference}
                                        onChange={(e) => setBankReference(e.target.value)}
                                        placeholder="Transfer reference from the bank"
                                    />
                                </div>
                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>
                                        Cancel
                                    </button>
                                    <button
                                        className="swallet__btn swallet__btn--success"
                                        onClick={handleMarkPaid}
                                        disabled={processing || !bankReference.trim()}
                                    >
                                        {processing ? 'Processing...' : 'Confirm Paid'}
                                    </button>
                                </div>
                            </>
                        )}

                        {action.type === 'decline' && (
                            <>
                                <h3 className="swallet__modalTitle">Decline Payout</h3>
                                <p className="swallet__modalText">
                                    Decline the payout of <strong>{money(action.payout.amount)}</strong> for{' '}
                                    <strong>{action.payout.client_name || `Client #${action.payout.client_id}`}</strong>?
                                    The reserved amount becomes spendable again.
                                </p>
                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">Reason (required)</label>
                                    <textarea
                                        className="swallet__formTextarea"
                                        style={{ height: 60 }}
                                        value={declineNote}
                                        onChange={(e) => setDeclineNote(e.target.value)}
                                        placeholder="e.g. IBAN doesn't match the account name…"
                                    />
                                </div>
                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>
                                        Cancel
                                    </button>
                                    <button
                                        className="swallet__btn swallet__btn--danger"
                                        onClick={handleDecline}
                                        disabled={processing || !declineNote.trim()}
                                    >
                                        {processing ? 'Processing...' : 'Confirm Decline'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* The bank transfer was sent and the wallet debit FAILED. The row is
                held in 'processing' by the backend so it cannot go out twice.
                Deliberately not dismissible by clicking away. */}
            {debitFailure && (
                <div className="swallet__overlay">
                    <div className="swallet__modal">
                        <h3 className="swallet__modalTitle">Transfer sent — wallet NOT debited</h3>
                        <div className="swallet__payoutWarn">
                            {debitFailure.error}
                        </div>
                        <p className="swallet__modalText">
                            Payout <strong>#{debitFailure.payout.id}</strong> of{' '}
                            <strong>{money(debitFailure.payout.amount)}</strong> for{' '}
                            <strong>{debitFailure.payout.client_name || `Client #${debitFailure.payout.client_id}`}</strong>{' '}
                            (bank ref <strong>{debitFailure.bankReference}</strong>).
                        </p>
                        <p className="swallet__modalText">
                            The payout has been <strong>held</strong> — it has NOT returned to the queue, so it cannot be
                            transferred a second time. Do not send another transfer. Reconcile the client&apos;s wallet
                            (the cash balance is short), then reopen this payout and mark it paid again with the same
                            bank reference.
                        </p>
                        <div className="swallet__modalActions">
                            <Link
                                to={`/wallet/clients/${debitFailure.payout.client_id}`}
                                className="swallet__btn swallet__btn--primary"
                                onClick={() => setDebitFailure(null)}
                            >
                                Open the client&apos;s wallet
                            </Link>
                            <button className="swallet__btn" onClick={() => setDebitFailure(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
